import { Link } from "react-router-dom";
import { useMemoryResurface, type Memory } from "@/lib/useMemoryResurface";

/**
 * MemoryLayer — the dashboard's continuity surface. Renders up to three
 * reflective lines composed server-side. Each line is editorial typography,
 * never an alert. The section hides entirely when the server returns no
 * memories so the surface stays empty by absence rather than placeholder.
 *
 * Intentionally inert visually: no icons, no chips, no avatars. Hairline
 * dividers between memories so the rhythm reads like a footer of a journal,
 * not a notification panel.
 */
export function MemoryLayer() {
  const { data, isLoading } = useMemoryResurface();
  if (isLoading) return null;
  const memories = data?.memories ?? [];
  if (memories.length === 0) return null;

  return (
    <section className="mt-12 first:mt-0">
      <header className="mb-4">
        <p className="label-eyebrow">Continuity</p>
        <h2 className="mt-1.5 font-display text-xl tracking-tight">You've been here</h2>
      </header>
      <div className="panel p-6 divide-y divide-veil/30">
        {memories.map((m, i) => (
          <MemoryLine key={`${m.kind}-${i}`} memory={m} isFirst={i === 0} />
        ))}
      </div>
    </section>
  );
}

function MemoryLine({ memory, isFirst }: { memory: Memory; isFirst: boolean }) {
  const body = (
    <div className={isFirst ? "pb-4" : "py-4 last:pb-0"}>
      <p className="text-[10px] uppercase tracking-[0.18em] text-gold-glow/60">
        {memory.eyebrow}
      </p>
      <p className="mt-1 text-sm text-fog leading-relaxed">{memory.message}</p>
    </div>
  );
  if (memory.link) {
    return (
      <Link
        to={memory.link}
        className="block hover:bg-veil/15 transition-colors -mx-2 px-2 rounded"
      >
        {body}
      </Link>
    );
  }
  return body;
}
